import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CreditCard, Trash2, Zap } from "lucide-react";

function loadSquare() {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const checkInterval = setInterval(() => {
      if (window.Square) {
        clearInterval(checkInterval);
        resolve(window.Square);
      }
    }, 100);
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.Square) reject(new Error("Square SDK failed to load"));
    }, 10000);
  });
}

export default function AutoRefillCard({ user, onUpdate }) {
  const cardMountRef = useRef(null);
  const cardInstanceRef = useRef(null);
  const paymentsRef = useRef(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [threshold, setThreshold] = useState(user.refill_threshold || 50);
  const [amount, setAmount] = useState(user.refill_amount || 100);
  const [enabled, setEnabled] = useState(user.auto_refill_enabled || false);

  const hasCard = !!user.card_last_4;

  // Mount the Square card form only when no card is saved
  useEffect(() => {
    if (hasCard) return;
    let canceled = false;
    (async () => {
      try {
        const Square = await loadSquare();
        const meta = (await api.get("/meta/square")).data;
        if (!meta.application_id || !meta.location_id) {
          toast.error("Square not configured");
          return;
        }
        const payments = Square.payments(meta.application_id, meta.location_id);
        if (canceled) return;
        paymentsRef.current = payments;
        const card = await payments.card();
        if (canceled) return;
        if (cardMountRef.current) {
          await card.attach(cardMountRef.current);
          cardInstanceRef.current = card;
          setSdkReady(true);
        }
      } catch (err) {
        console.error("Square SDK init failed:", err);
        toast.error("Could not initialize payment form. Refresh the page.");
      }
    })();
    return () => {
      canceled = true;
      try {
        cardInstanceRef.current?.destroy?.();
      } catch (err) {
        console.error("Card destroy error:", err);
      }
    };
  }, [hasCard]);

  const saveCard = async () => {
    if (!cardInstanceRef.current) return;
    setBusy(true);
    try {
      const tokenResult = await cardInstanceRef.current.tokenize();
      if (tokenResult.status !== "OK") {
        const msg =
          tokenResult.errors?.[0]?.message ||
          `Card entry failed (${tokenResult.status})`;
        toast.error(msg);
        return;
      }
      // Verify buyer with STORE intent (SCA / 3DS)
      let verificationToken;
      try {
        const verify = await paymentsRef.current.verifyBuyer(tokenResult.token, {
          intent: "STORE",
          billingContact: {
            givenName: (user.name || "").split(" ")[0] || "Agent",
            familyName: (user.name || "").split(" ").slice(1).join(" ") || "User",
            email: user.email,
          },
        });
        verificationToken = verify?.token;
      } catch (err) {
        console.error("verifyBuyer error:", err);
      }
      const { data } = await api.post("/payments/save-card", {
        source_id: tokenResult.token,
        verification_token: verificationToken,
      });
      toast.success("Card saved. Auto-refill enabled.");
      onUpdate(data.user);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeCard = async () => {
    if (!window.confirm("Remove saved card? Auto-refill will be disabled.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/remove-card");
      toast.success("Card removed");
      onUpdate(data.user);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async (overrides = {}) => {
    const payload = {
      enabled: overrides.enabled ?? enabled,
      threshold: Number(overrides.threshold ?? threshold),
      amount: Number(overrides.amount ?? amount),
    };
    try {
      const { data } = await api.put("/payments/auto-refill", payload);
      onUpdate(data.user);
      if (overrides.enabled !== undefined) {
        toast.success(payload.enabled ? "Auto-refill ON" : "Auto-refill OFF");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      // revert local
      setEnabled(user.auto_refill_enabled || false);
    }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="auto-refill-card">
      <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#0055FF]" />
          <div className="font-heading font-bold tracking-tight text-lg">Auto-refill</div>
        </div>
        <div className="text-xs text-zinc-500">
          Wallet tops up automatically when balance drops below your threshold.
        </div>
      </div>

      {/* Card on file */}
      <div className="p-6 border-b border-zinc-200">
        {hasCard ? (
          <div className="flex items-center justify-between" data-testid="card-on-file">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-zinc-700" />
              <div>
                <div className="font-mono font-bold text-base">
                  {(user.card_brand || "CARD").toUpperCase()} •••• {user.card_last_4}
                </div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-0.5">
                  Saved securely · Will charge automatically
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={removeCard}
              disabled={busy}
              className="btn-sharp text-rose-600"
              data-testid="remove-card-button"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
            </Button>
          </div>
        ) : (
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-600 mb-2 block">
              Add a card on file
            </Label>
            <div
              ref={cardMountRef}
              id="square-card-container"
              className="border border-zinc-300 p-3 min-h-[80px] bg-zinc-50"
              data-testid="square-card-container"
            />
            <Button
              onClick={saveCard}
              disabled={busy || !sdkReady}
              className="mt-3 w-full btn-sharp bg-black hover:bg-zinc-800 text-white font-bold uppercase tracking-wider text-xs h-11"
              data-testid="save-card-button"
            >
              {busy ? "Saving…" : sdkReady ? "Save card securely" : "Loading…"}
            </Button>
            <div className="text-[11px] text-zinc-500 mt-2 font-mono uppercase tracking-wider">
              PCI-secure · 256-bit TLS · Tokenized — never stored on our servers
            </div>
          </div>
        )}
      </div>

      {/* Settings */}
      {hasCard && (
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono font-bold text-sm">Auto-refill</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {enabled ? "Enabled — wallet will top up automatically" : "Disabled — top up manually"}
              </div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={(v) => {
                setEnabled(v);
                saveSettings({ enabled: v });
              }}
              data-testid="auto-refill-toggle"
            />
          </div>

          {enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-600">
                  When balance drops below
                </Label>
                <div className="mt-1 flex">
                  <span className="inline-flex items-center px-3 bg-zinc-100 border border-r-0 border-zinc-300 font-mono text-zinc-500">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    onBlur={() => saveSettings()}
                    className="btn-sharp rounded-none font-mono"
                    data-testid="refill-threshold-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-600">
                  Charge this amount
                </Label>
                <div className="mt-1 flex">
                  <span className="inline-flex items-center px-3 bg-zinc-100 border border-r-0 border-zinc-300 font-mono text-zinc-500">$</span>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    onBlur={() => saveSettings()}
                    className="btn-sharp rounded-none font-mono"
                    data-testid="refill-amount-input"
                  />
                </div>
              </div>
            </div>
          )}

          {user.auto_refill_last_error && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 p-3 font-mono">
              ⚠ Last auto-refill attempt failed — card may have been declined. Update your card to re-enable.
              <div className="text-[10px] mt-1 text-rose-600 break-all">{user.auto_refill_last_error}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
